import { useState, useEffect } from "react";
import { fetchDailyModulesCompleted } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Daily Modules Completed modal.
 *
 * Fires once on mount. Returns a full current-month daily breakdown scoped to
 * the current UTC calendar month. Future days carry actual: null and
 * status: "Future" so the chart and table render consistently for the full
 * month at any point during the month.
 *
 * Cancels the in-flight request automatically if the component unmounts before
 * the response arrives (AbortController cleanup).
 *
 * Cache TTL: 5 minutes - daily completion data may update several times per
 * day as modules are closed out, but the modal may be opened repeatedly within
 * a session.
 *
 * Note: dailyTarget is derived server-side from scheduled modules this month:
 *   CEIL(scheduledThisMonth / workingDaysThisMonth)
 * When no scheduled data exists the fallback comes from RTLS_ProductionGoals
 * (PrimaryName = 'monthlyMods', e.g. 2.00 modules/day).
 *
 * Status thresholds per day (applied server-side):
 *   "Met"     actual >= dailyTarget
 *   "Near"    actual >= dailyTarget × 0.75
 *   "Missed"  actual < dailyTarget × 0.75
 *   "Future"  day has not yet occurred
 *
 * @returns {{
 *   metrics: {
 *     monthAvgPct:  number,   - avg of daily (actual/target)% across elapsed days, 1 dp
 *     daysMet:      number,   - days with status "Met"
 *     daysNear:     number,   - days with status "Near"
 *     daysMissed:   number,   - days with status "Missed"
 *     dailyTarget:  number,   - modules/day target (derived or goal fallback)
 *   },
 *   graph: Array<{
 *     day:    number,          - 1-based day of month
 *     actual: number | null,   - null for future days
 *     target: number,          - dailyTarget echoed on every entry
 *   }>,
 *   table: Array<{
 *     day:    number,                              - 1-based day of month
 *     date:   string,                              - "YYYY-MM-DD"
 *     target: number,
 *     actual: number | null,                       - null for future days
 *     pct:    number | null,                       - actual/target × 100, 1 dp; null for future
 *     status: "Met" | "Near" | "Missed" | "Future",
 *   }>,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 5 * 60_000; // 5 minutes

const METRICS_DEFAULT = {
  monthAvgPct: 0,
  daysMet: 0,
  daysNear: 0,
  daysMissed: 0,
  dailyTarget: 2,
};

const useDailyModulesCompletedData = () => {
  const [metrics, setMetrics] = useState(_cache?.metrics ?? METRICS_DEFAULT);
  const [graph, setGraph] = useState(_cache?.graph ?? []);
  const [table, setTable] = useState(_cache?.table ?? []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // already fresh - skip fetch

    const load = async () => {
      try {
        const data = await fetchDailyModulesCompleted(controller.signal);
        _cache = data;
        _cacheTime = Date.now();
        setMetrics(data.metrics);
        setGraph(data.graph);
        setTable(data.table);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load daily modules completed data.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return { metrics, graph, table, loading, error };
};

export default useDailyModulesCompletedData;
